import { useSyncExternalStore } from 'react'
import type { ChatUser } from '../types/chat'
import type { CallMediaType } from '../types/voice-call'

/**
 * An OUTGOING call attempt by a Member/Independent who has no durable
 * recording-policy acceptance yet. The top-level <RecordingPolicyGateDialog/>
 * shows the recording notice BEFORE dialing; on accept it records the decision
 * and calls `proceed`, which re-runs the original call start (same peer, same
 * media) — the user never has to redial.
 */
export type PendingRecordingPolicyGate = {
  /** The 1:1 target (absent for the leadership urgent ring). */
  peer?: ChatUser | null
  mediaType: CallMediaType
  /** Re-run the original call start, bypassing this gate (after accept). */
  proceed: () => void
}

let pending: PendingRecordingPolicyGate | null = null
const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) listener()
}

function getPendingRecordingPolicyGate(): PendingRecordingPolicyGate | null {
  return pending
}

export function setPendingRecordingPolicyGate(next: PendingRecordingPolicyGate | null): void {
  pending = next
  emit()
}

export function clearPendingRecordingPolicyGate(): void {
  if (pending === null) return
  pending = null
  emit()
}

export function usePendingRecordingPolicyGate(): PendingRecordingPolicyGate | null {
  return useSyncExternalStore(
    subscribe,
    getPendingRecordingPolicyGate,
    getPendingRecordingPolicyGate,
  )
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
